import { useCallback, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { fmtAgo, fmtDT, fmtGB, keyLabel, restartAndWait } from "@/lib/localServer";
import { useAuth } from "@/context/AuthContext";
import { DrivePicker } from "@/components/DrivePicker";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Server, RefreshCw, HardDrive, Wifi, Users, Copy, AlertTriangle, KeyRound, Trash2, Loader2, RotateCcw, Cloud, CloudOff, Download, ArrowUpCircle } from "lucide-react";

const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast.success("Copied"), () => toast.error("Copy failed"));

function Panel({ title, icon: Icon, children, action, testId }) {
  return (
    <section className="border border-border bg-card p-5" data-testid={testId}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="font-display font-bold uppercase tracking-tight text-lg flex items-center gap-2"><Icon className="w-4 h-4 text-primary" />{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function Stat({ label, value, tone = "", testId }) {
  return (
    <div className="border border-border p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className={`font-display font-black text-2xl mt-1 ${tone}`} data-testid={testId}>{value}</div>
    </div>
  );
}

function Header({ eyebrow, sub, children }) {
  return (
    <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
      <div>
        <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2">{eyebrow}</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter" data-testid="local-server-title">Shop Server &amp; Sync</h1>
        <div className="text-xs text-muted-foreground mt-2 max-w-2xl">{sub}</div>
      </div>
      {children}
    </div>
  );
}

function Table({ cols, rows, empty, testId }) {
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className="w-full text-sm">
        <thead><tr className="border-b border-border">{cols.map(c => <th key={c} className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-medium py-2 pr-3">{c}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={cols.length} className="py-6 text-center text-muted-foreground text-xs">{empty}</td></tr>}
          {rows}
        </tbody>
      </table>
    </div>
  );
}

function DevicesPanel() {
  const [d, setD] = useState({ items: [] });
  useEffect(() => {
    const load = () => api.get("/local/devices").then(r => setD(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  return (
    <Panel title="Connected Users & Devices" icon={Users} testId="devices-panel"
      action={<span className="text-xs text-muted-foreground" data-testid="devices-count">{d.users || 0} users · {d.devices || 0} devices · last {d.window_minutes || 15} min</span>}>
      <Table testId="devices-table" cols={["User", "Role", "Device", "IP Address", "Last Active"]} empty="No active users right now"
        rows={d.items.map((r, i) => (
          <tr key={`${r.ip}-${r.user_id}`} className="border-b border-border/50" data-testid={`device-row-${i}`}>
            <td className="py-2 pr-3"><div className="font-medium">{r.user_name || "—"}</div><div className="text-xs text-muted-foreground">{r.email}</div></td>
            <td className="py-2 pr-3 capitalize">{(r.role || "").replace("_", " ")}</td>
            <td className="py-2 pr-3">{r.device}</td>
            <td className="py-2 pr-3 font-mono text-xs">{r.ip}</td>
            <td className="py-2 pr-3 text-xs">{fmtAgo(r.last_seen)}</td>
          </tr>
        ))} />
    </Panel>
  );
}

function ConflictsPanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/local/conflicts").then(r => setRows(r.data.items)).catch(() => {}); }, []);
  return (
    <Panel title="Conflict Log" icon={AlertTriangle} testId="conflicts-panel"
      action={<span className="text-xs text-muted-foreground">Most recent change wins</span>}>
      <Table testId="conflicts-table" cols={["When", "Record", "Kept", "Shop change", "Cloud change"]} empty="No conflicts recorded"
        rows={rows.map((c, i) => (
          <tr key={c.id} className="border-b border-border/50" data-testid={`conflict-row-${i}`}>
            <td className="py-2 pr-3 text-xs">{fmtDT(c.at)}</td>
            <td className="py-2 pr-3"><span className="capitalize">{c.coll.replace(/_/g, " ")}</span> <span className="font-mono text-xs text-muted-foreground">#{keyLabel(c.key)}</span></td>
            <td className="py-2 pr-3"><Badge variant="outline" className={c.winner === "cloud" ? "border-sky-500/50 text-sky-400" : "border-primary/50 text-primary"}>{c.winner === "cloud" ? "Cloud version" : "Shop version"}</Badge></td>
            <td className="py-2 pr-3 text-xs">{fmtDT(c.local_ts)}</td>
            <td className="py-2 pr-3 text-xs">{fmtDT(c.remote_ts)}</td>
          </tr>
        ))} />
    </Panel>
  );
}

function SyncPanel({ s, reload }) {
  const [busy, setBusy] = useState("");
  const run = async (path, label) => {
    setBusy(label);
    try {
      const { data } = await api.post(path);
      if (data.ok) toast.success(`Synced — ${data.pushed} sent, ${data.pulled} received`);
      else toast.error(data.error || "Sync skipped");
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(""); reload(); }
  };
  const q = s.queue;
  const offline = s.online === false;
  return (
    <Panel title="Cloud Sync" icon={offline ? CloudOff : Cloud} testId="sync-panel"
      action={<div className="flex gap-2">
        {q.failed > 0 && <Button size="sm" variant="outline" onClick={() => run("/local/retry-failed", "retry")} disabled={!!busy} data-testid="retry-failed-btn"><RotateCcw className="w-3.5 h-3.5 mr-1.5" />Retry failed</Button>}
        <Button size="sm" onClick={() => run("/local/sync-now", "now")} disabled={!!busy} data-testid="sync-now-btn">
          {busy === "now" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}Sync now
        </Button>
      </div>}>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Badge data-testid="sync-online-badge" className={offline ? "bg-destructive/20 text-destructive border border-destructive/40" : "bg-emerald-500/15 text-emerald-400 border border-emerald-500/40"}>{offline ? "Cloud unreachable — working offline" : "Connected to cloud"}</Badge>
        <span className="text-xs text-muted-foreground">Linked to <b className="text-foreground" data-testid="sync-org-name">{s.org_name}</b> · auto-sync every {s.interval_seconds}s</span>
      </div>
      {s.last_error && offline && (
        <div className="border border-destructive/40 bg-destructive/10 text-sm p-3 mb-4 flex gap-2" data-testid="sync-error-alert">
          <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
          <div>{s.last_error} · {s.consecutive_failures} failed attempt(s). Changes stay safe on this computer and will send automatically when the internet is back.</div>
        </div>
      )}
      {q.failed > 0 && (
        <div className="border border-yellow-500/40 bg-yellow-500/10 text-sm p-3 mb-4 flex gap-2" data-testid="sync-failed-alert">
          <AlertTriangle className="w-4 h-4 text-yellow-500 shrink-0 mt-0.5" /><div>{q.failed} change(s) were refused by the cloud. Open the Failed tab below to see why.</div>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Last successful sync" value={fmtAgo(s.last_success)} testId="sync-last-success" />
        <Stat label="Waiting to send" value={q.pending} tone={q.pending ? "text-yellow-500" : ""} testId="sync-pending-count" />
        <Stat label="Failed" value={q.failed} tone={q.failed ? "text-destructive" : ""} testId="sync-failed-count" />
        <Stat label="Conflicts" value={q.conflict} testId="sync-conflict-count" />
      </div>
      <div className="text-xs text-muted-foreground mt-3">Last attempt {fmtDT(s.last_attempt)} · last run sent {s.last_pushed ?? 0}, received {s.last_pulled ?? 0}{q.oldest_pending ? ` · oldest waiting change from ${fmtDT(q.oldest_pending)}` : ""}</div>
      {s.snapshot?.status === "running" && <div className="text-xs text-primary mt-2" data-testid="snapshot-progress">Downloading establishment data: {s.snapshot.done}/{s.snapshot.total}</div>}
    </Panel>
  );
}

function QueuePanel() {
  const [tab, setTab] = useState("pending");
  const [d, setD] = useState({ items: [], total: 0 });
  useEffect(() => { api.get("/local/queue", { params: { status: tab } }).then(r => setD(r.data)).catch(() => {}); }, [tab]);
  return (
    <Panel title="Offline Queue" icon={RefreshCw} testId="queue-panel"
      action={<Tabs value={tab} onValueChange={setTab}><TabsList>
        {["pending", "failed", "conflict", "synced"].map(t => <TabsTrigger key={t} value={t} className="capitalize text-xs" data-testid={`queue-tab-${t}`}>{t}</TabsTrigger>)}
      </TabsList></Tabs>}>
      <div className="text-xs text-muted-foreground mb-2" data-testid="queue-total">{d.total} {tab} change(s){d.total > d.items.length ? ` · showing latest ${d.items.length}` : ""}</div>
      <Table testId="queue-table" cols={["#", "Record", "Action", "Changed", "Attempts", "Note"]} empty={`No ${tab} changes`}
        rows={d.items.map(e => (
          <tr key={e.seq} className="border-b border-border/50">
            <td className="py-2 pr-3 font-mono text-xs">{e.seq}</td>
            <td className="py-2 pr-3"><span className="capitalize">{e.coll.replace(/_/g, " ")}</span> <span className="font-mono text-xs text-muted-foreground">#{keyLabel(e.key)}</span></td>
            <td className="py-2 pr-3 text-xs uppercase">{e.op === "delete" ? "Delete" : "Save"}</td>
            <td className="py-2 pr-3 text-xs">{fmtDT(e.ts)}</td>
            <td className="py-2 pr-3 text-xs">{e.attempts}</td>
            <td className="py-2 pr-3 text-xs text-muted-foreground max-w-xs truncate">{e.error || "—"}</td>
          </tr>
        ))} />
    </Panel>
  );
}

function StoragePanel({ s, reload }) {
  const [open, setOpen] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const st = s.storage;
  return (
    <Panel title="Data Storage" icon={HardDrive} testId="storage-panel"
      action={<Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="change-storage-btn">Change location</Button>}>
      <div className="font-mono text-sm break-all" data-testid="storage-path">{st.data_root || "Not chosen yet"}</div>
      {st.usage && <div className="mt-3">
        <div className="flex justify-between text-xs text-muted-foreground"><span>Drive {st.drive}</span><span data-testid="storage-free">{fmtGB(st.usage.free)} free of {fmtGB(st.usage.total)}</span></div>
        <div className="h-2 bg-muted mt-1.5"><div className="h-full bg-primary" style={{ width: `${st.usage.percent}%` }} /></div>
      </div>}
      {st.restart_required && <div className="text-xs text-yellow-500 mt-3 flex items-center justify-between gap-3 flex-wrap" data-testid="storage-restart-note">
        <span>New location saved: {st.pending_db_path}. Restart the shop server to move the data there.</span>
        <Button size="sm" variant="outline" disabled={restarting} data-testid="storage-restart-btn"
          onClick={async () => { setRestarting(true); const ok = await restartAndWait(); setRestarting(false); toast[ok ? "success" : "error"](ok ? "Shop server restarted" : "Server did not come back yet — refresh in a minute"); reload(); }}>
          {restarting && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}Restart now</Button>
      </div>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-card">
          <DialogHeader><DialogTitle>Choose storage location</DialogTitle><DialogDescription>Pick a drive on this computer and a folder name for the ERP data.</DialogDescription></DialogHeader>
          <DrivePicker initialFolder={(st.data_root || "").split(/[\\/]/).pop() || "SynqMotoData"} onSaved={() => { setOpen(false); reload(); }} />
        </DialogContent>
      </Dialog>
    </Panel>
  );
}

function NetworkPanel({ s }) {
  const n = s.network;
  return (
    <Panel title="Local Network Access" icon={Wifi} testId="network-panel">
      <div className="text-xs text-muted-foreground mb-3">Other computers and phones on the same WiFi/LAN open this address in a browser:</div>
      <div className="space-y-2">
        {n.urls.map((u, i) => (
          <div key={u} className={`flex items-center justify-between border p-2.5 ${i === 0 ? "border-primary/60" : "border-border"}`}>
            <span className="font-mono text-sm" data-testid={`network-url-${i}`}>{u}</span>
            <Button size="sm" variant="ghost" onClick={() => copy(u)} data-testid={`copy-url-${i}`}><Copy className="w-3.5 h-3.5" /></Button>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3 mt-4 text-xs">
        <div><div className="text-muted-foreground uppercase tracking-[0.15em] text-[10px]">IP</div><div className="font-mono" data-testid="network-ip">{n.primary_ip || "—"}</div></div>
        <div><div className="text-muted-foreground uppercase tracking-[0.15em] text-[10px]">Port</div><div className="font-mono" data-testid="network-port">{n.port}</div></div>
        <div><div className="text-muted-foreground uppercase tracking-[0.15em] text-[10px]">Computer</div><div className="font-mono truncate">{n.hostname}</div></div>
      </div>
    </Panel>
  );
}

function UpdatePanel() {
  const [u, setU] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/local/update").then(r => setU(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    if (u?.update?.status !== "restarting") return;
    const t = setInterval(() => api.get("/local/public-status", { timeout: 2000 }).then(() => api.get("/local/update")).then(r => {
      if (r.data.update?.status !== "restarting") window.location.reload();
    }).catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [u?.update?.status]);
  if (!u) return null;
  const st = u.update?.status;
  const working = u.running || ["syncing", "backing_up", "downloading", "restarting"].includes(st);
  const start = async () => {
    if (!window.confirm("Update the shop server now? Waiting changes are sent to the cloud and the data is backed up first. The app is unavailable for a few minutes while it restarts.")) return;
    setBusy(true);
    try { await api.post("/local/update"); toast.success("Update started"); load(); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const tone = st === "failed" || st === "rolled_back" ? "text-destructive" : st === "done" ? "text-emerald-400" : "text-primary";
  return (
    <Panel title="App Version" icon={ArrowUpCircle} testId="update-panel"
      action={(u.update_available || st === "failed" || st === "rolled_back") && !working &&
        <Button size="sm" onClick={start} disabled={busy} data-testid="update-now-btn"><ArrowUpCircle className="w-3.5 h-3.5 mr-1.5" />Update now</Button>}>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        <div>This server: <span className="font-mono" data-testid="update-current-version">{u.version}</span></div>
        <div>Latest: <span className="font-mono" data-testid="update-latest-version">{u.latest_version || "—"}</span>{u.latest_released_at && <span className="text-xs text-muted-foreground"> · {fmtDT(u.latest_released_at)}</span>}</div>
        {u.update_available && !working && <Badge className="bg-primary/15 text-primary border border-primary/40" data-testid="update-available-badge">New version available</Badge>}
        {!u.update_available && !st && <span className="text-xs text-emerald-400">Up to date</span>}
      </div>
      {st && <div className={`text-sm mt-3 flex items-center gap-2 ${tone}`} data-testid="update-status">
        {working && <Loader2 className="w-4 h-4 animate-spin" />}
        <span>{u.update.label}{u.update.error ? ` — ${u.update.error}` : ""}</span>
        <span className="text-xs text-muted-foreground">{fmtAgo(u.update.at)}</span>
      </div>}
      {u.update?.backup && !working && <div className="text-xs text-muted-foreground mt-1 font-mono break-all">Backup: {u.update.backup}</div>}
    </Panel>
  );
}

function LocalView({ s, reload }) {
  return (
    <div className="space-y-5">
      <UpdatePanel />
      <SyncPanel s={s} reload={reload} />
      <div className="grid lg:grid-cols-2 gap-5">
        <StoragePanel s={s} reload={reload} />
        <NetworkPanel s={s} />
      </div>
      <DevicesPanel />
      <QueuePanel />
      <ConflictsPanel />
    </div>
  );
}

function NewKeyDialog({ open, onClose, onCreated }) {
  const { isSuperAdmin } = useAuth();
  const [name, setName] = useState("Shop Server");
  const [orgs, setOrgs] = useState([]);
  const [org, setOrg] = useState("");
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open && isSuperAdmin) api.get("/sync/orgs").then(r => setOrgs(r.data.items)).catch(() => {});
    if (open) setCreated(null);
  }, [open, isSuperAdmin]);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sync/devices", { name, org_id: org || null });
      setCreated(data); onCreated();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const download = async () => {
    setBusy(true);
    try {
      const cloud_url = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const res = await api.post("/sync/installer", { name, org_id: org || null, cloud_url }, { responseType: "blob" });
      const fname = (res.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "SynqMoto-Setup.exe";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = fname;
      a.click();
      URL.revokeObjectURL(a.href);
      setCreated({ installer: fname }); onCreated();
    } catch (e) {
      const msg = e?.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : formatErr(e);
      toast.error(msg || "Download failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="new-key-dialog">
        <DialogHeader><DialogTitle>Link a shop server</DialogTitle><DialogDescription>Download a ready-to-run Windows installer for the shop computer, or create a sync key to enter by hand.</DialogDescription></DialogHeader>
        {created?.installer ? (
          <div className="space-y-3" data-testid="installer-downloaded">
            <div className="text-sm">Installer downloaded: <span className="font-mono text-primary">{created.installer}</span></div>
            <ol className="text-sm list-decimal pl-5 space-y-1 text-muted-foreground">
              <li>Copy the file to the shop computer (Windows 10/11, 64-bit) and double-click it.</li>
              <li>Click Next, choose the drive and folder for ERP data, then Install. Internet is needed during setup (about 10 minutes).</li>
              <li>When it finishes the shop server is running and linked. Open <b className="text-foreground">SynqMoto ERP</b> from the desktop and sign in.</li>
            </ol>
            <div className="text-xs text-yellow-500">The file contains this shop's sync key. Keep it private and delete it after installing.</div>
            <Button onClick={onClose} className="w-full" data-testid="new-key-done">Done</Button>
          </div>
        ) : created ? (
          <div className="space-y-3">
            <div className="text-sm">Enter these on the shop server's setup screen. <b>The key is shown only once.</b></div>
            <div className="border border-border p-3 space-y-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Cloud address</div>
              <div className="flex items-center justify-between gap-2"><span className="font-mono text-xs break-all" data-testid="new-key-cloud-url">{process.env.REACT_APP_BACKEND_URL || window.location.origin}</span><Button size="sm" variant="ghost" onClick={() => copy(process.env.REACT_APP_BACKEND_URL || window.location.origin)}><Copy className="w-3.5 h-3.5" /></Button></div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground pt-2">Sync key</div>
              <div className="flex items-center justify-between gap-2"><span className="font-mono text-xs break-all text-primary" data-testid="new-key-value">{created.sync_key}</span><Button size="sm" variant="ghost" onClick={() => copy(created.sync_key)} data-testid="copy-new-key"><Copy className="w-3.5 h-3.5" /></Button></div>
            </div>
            <Button onClick={onClose} className="w-full" data-testid="new-key-done">Done</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-[0.15em]">Server name</Label><Input value={name} onChange={e => setName(e.target.value)} data-testid="new-key-name" /></div>
            {isSuperAdmin && <div className="space-y-1.5"><Label className="text-xs uppercase tracking-[0.15em]">Establishment</Label>
              <Select value={org} onValueChange={setOrg}><SelectTrigger data-testid="new-key-org"><SelectValue placeholder="Select establishment" /></SelectTrigger>
                <SelectContent>{orgs.map(o => <SelectItem key={o.id} value={o.id} data-testid={`new-key-org-${o.id}`}>{o.business_name}</SelectItem>)}</SelectContent></Select></div>}
            <Button onClick={download} disabled={busy || !name.trim() || (isSuperAdmin && !org)} className="w-full" data-testid="download-installer-btn">{busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}Download Windows installer</Button>
            <Button variant="outline" onClick={create} disabled={busy || !name.trim() || (isSuperAdmin && !org)} className="w-full" data-testid="new-key-create">Create sync key only</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const UPDATE_LABEL = { requested: "Update requested", syncing: "Updating…", backing_up: "Updating…", downloading: "Updating…", restarting: "Updating…", done: "Updated", failed: "Update failed", rolled_back: "Update undone" };

function ShopVersion({ d, i, onUpdate }) {
  const outdated = d.version && d.latest_version && d.version !== d.latest_version;
  const busy = ["requested", "syncing", "backing_up", "downloading", "restarting"].includes(d.update_status);
  return (
    <div className="space-y-1">
      <div className="font-mono">{d.version || "—"}</div>
      {d.update_status && <div className={d.update_status === "failed" || d.update_status === "rolled_back" ? "text-destructive" : "text-muted-foreground"} title={d.update_error || ""} data-testid={`shop-server-update-status-${i}`}>{UPDATE_LABEL[d.update_status] || d.update_status}</div>}
      {outdated && !busy && <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => onUpdate(d)} data-testid={`request-update-${i}`}><ArrowUpCircle className="w-3 h-3 mr-1" />Update</Button>}
    </div>
  );
}

function CloudView() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/sync/devices").then(r => setRows(r.data.items)).catch(e => toast.error(formatErr(e))), []);
  useEffect(() => { load(); }, [load]);
  const revoke = async (d) => {
    if (!window.confirm(`Revoke "${d.name}"? That shop server will stop syncing.`)) return;
    try { await api.delete(`/sync/devices/${d.id}`); toast.success("Shop server revoked"); load(); } catch (e) { toast.error(formatErr(e)); }
  };
  const requestUpdate = async (d) => {
    try { await api.post(`/sync/devices/${d.id}/request-update`); toast.success(`Update requested — ${d.name} starts within about a minute if it is online`); load(); } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="space-y-5">
      <Panel title="Linked Shop Servers" icon={Server} testId="shop-servers-panel"
        action={<Button size="sm" onClick={() => setOpen(true)} data-testid="new-key-btn"><KeyRound className="w-3.5 h-3.5 mr-1.5" />Link shop server</Button>}>
        <Table testId="shop-servers-table" cols={["Name", "Establishment", "Computer", "Last sync", "Waiting to send", "Version", "Key", ""]} empty="No shop servers linked yet"
          rows={rows.map((d, i) => (
            <tr key={d.id} className="border-b border-border/50" data-testid={`shop-server-row-${i}`}>
              <td className="py-2 pr-3 font-medium">{d.name}</td>
              <td className="py-2 pr-3">{d.org_name}</td>
              <td className="py-2 pr-3 text-xs"><div>{d.hostname || "Not set up yet"}</div><div className="font-mono text-muted-foreground">{d.local_ip}</div></td>
              <td className="py-2 pr-3 text-xs" data-testid={`shop-server-last-sync-${i}`}>{fmtAgo(d.last_pull_at || d.last_seen)}</td>
              <td className="py-2 pr-3 text-xs">{d.pending_for_device ?? "—"}</td>
              <td className="py-2 pr-3 text-xs" data-testid={`shop-server-version-${i}`}><ShopVersion d={d} i={i} onUpdate={requestUpdate} /></td>
              <td className="py-2 pr-3 font-mono text-xs">{d.key_prefix}…</td>
              <td className="py-2 text-right"><Button size="sm" variant="ghost" onClick={() => revoke(d)} data-testid={`revoke-shop-server-${i}`}><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button></td>
            </tr>
          ))} />
        <div className="text-xs text-muted-foreground mt-4 leading-relaxed">
          To run SynqMoto on a shop computer: click <b>Link shop server</b>, download the Windows installer and run it on the shop PC. The setup wizard asks where to keep the ERP data, installs everything, starts automatically whenever the PC turns on, and keeps the shop working without internet, syncing with the cloud every minute.
        </div>
      </Panel>
      <DevicesPanel />
      <ConflictsPanel />
      <NewKeyDialog open={open} onClose={() => setOpen(false)} onCreated={load} />
    </div>
  );
}

export default function LocalServer() {
  const [s, setS] = useState(null);
  const load = useCallback(() => api.get("/local/status").then(r => setS(r.data)).catch(e => toast.error(formatErr(e))), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);
  if (!s) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  const local = s.mode === "local";
  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="local-server-page">
      <Header eyebrow={local ? "Shop Server · Running on this computer" : "Cloud ERP"}
        sub={local ? "This computer is the main server for your shop. Data is saved here first and sent to the cloud automatically every minute." : "Link shop computers that run SynqMoto locally. They keep working offline and sync with the cloud every minute."}>
        <Badge variant="outline" className="border-primary/50 text-primary uppercase tracking-[0.15em]" data-testid="deployment-mode-badge">{local ? "Shop server mode" : "Cloud mode"}</Badge>
      </Header>
      {local ? <LocalView s={s} reload={load} /> : <CloudView />}
    </div>
  );
}
